import { IconChart1 } from 'central-icons/IconChart1';
import classNames from 'classnames';
import {
  type CSSProperties,
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

import { ChartProvider } from '../Chart/ChartProvider';
import { Chart } from '../Chart/ChartRoot/Chart';
import { SpecialChartSelector } from '../Chart/ChartRoot/SpecialChart';
import { ChartMenuDataProvider } from '../Chart/chart-menu/ChartMenuContext';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import styles from './ReportFlow.module.css';
import type { ReportFlowBlock, ReportFlowLeafBlock } from './report-flow-types';

const useHeadingAnchors = (leaf: ReportFlowLeafBlock) => {
  const ref = useRef<HTMLDivElement>(null);
  const headingCount = leaf.headings.length;
  const blockId = leaf.id;

  useEffect(() => {
    const element = ref.current;
    if (!element || headingCount === 0) return;

    const assignIds = () => {
      element.querySelectorAll('h1, h2, h3, h4').forEach((heading, index) => {
        heading.id = `rf-${blockId}-h${index}`;
      });
    };

    assignIds();
    const observer = new MutationObserver(assignIds);
    observer.observe(element, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [blockId, headingCount]);

  return ref;
};

const BareLeafContent = ({ leaf }: { leaf: ReportFlowLeafBlock }) => (
  <ChartProvider item={leaf.item} variant="report">
    <ChartMenuDataProvider>
      <SpecialChartSelector />
    </ChartMenuDataProvider>
  </ChartProvider>
);

const LiveChartLeafContent = ({
  leaf,
  onChartClick,
}: {
  leaf: ReportFlowLeafBlock;
  onChartClick?: (itemId: string) => void;
}) => {
  const { config } = useTabContentConfig();
  if (!leaf.item.plot?.chartType) {
    return onChartClick ? (
      <button
        type="button"
        className={styles.emptyChart}
        onClick={() => onChartClick(leaf.item.id)}
        data-testid="report-flow-empty-chart"
      >
        <IconChart1 size={20} />
        Choose a chart
      </button>
    ) : null;
  }
  return (
    <Chart
      item={leaf.item}
      primaryFilter={config.primaryFilter}
      variant="report"
      {...(onChartClick && { onClick: () => onChartClick(leaf.item.id) })}
    />
  );
};

export type ReportFlowLeafContentRenderer = (
  leaf: ReportFlowLeafBlock,
  onChartClick?: (itemId: string) => void
) => ReactNode;

export const renderLiveLeafContent: ReportFlowLeafContentRenderer = (
  leaf,
  onChartClick
) =>
  leaf.kind === 'chart' ? (
    <LiveChartLeafContent leaf={leaf} onChartClick={onChartClick} />
  ) : (
    <BareLeafContent leaf={leaf} />
  );

const LeafBlockView = ({
  leaf,
  onChartClick,
  renderLeafContent,
}: {
  leaf: ReportFlowLeafBlock;
  onChartClick?: (itemId: string) => void;
  renderLeafContent: ReportFlowLeafContentRenderer;
}) => {
  const headingAnchorRef = useHeadingAnchors(leaf);

  const sizeStyle =
    leaf.heightPx === undefined
      ? undefined
      : ({ '--rf-block-height': `${leaf.heightPx}px` } as CSSProperties);

  const content = renderLeafContent(leaf, onChartClick);

  if (leaf.kind === 'text') {
    return (
      <div
        ref={headingAnchorRef}
        id={leaf.anchorId}
        className={classNames(styles.leaf, styles.textLeaf)}
        data-testid="report-flow-text-block"
      >
        {content}
      </div>
    );
  }

  if (leaf.kind === 'image') {
    return (
      <div
        id={leaf.anchorId}
        className={classNames(styles.leaf, styles.imageLeaf)}
        style={sizeStyle}
        data-testid="report-flow-image-block"
      >
        {content}
      </div>
    );
  }

  return (
    <div
      id={leaf.anchorId}
      className={classNames(styles.leaf, styles.chartLeaf)}
      style={sizeStyle}
      data-testid="report-flow-chart-block"
    >
      {content}
    </div>
  );
};

export const ReportFlowBlockView = ({
  block,
  onChartClick,
  columnWrapper,
  leafWrapper,
  renderLeafContent = renderLiveLeafContent,
}: {
  block: ReportFlowBlock;
  onChartClick?: (itemId: string) => void;
  /** Lets the editor own each column cell so a single column can be dragged. */
  columnWrapper?: (leaf: ReportFlowLeafBlock, content: ReactNode) => ReactNode;
  leafWrapper?: (leaf: ReportFlowLeafBlock, content: ReactNode) => ReactNode;
  renderLeafContent?: ReportFlowLeafContentRenderer;
}) => {
  if (block.kind !== 'columns') {
    const content = (
      <LeafBlockView
        leaf={block}
        onChartClick={onChartClick}
        renderLeafContent={renderLeafContent}
      />
    );
    if (leafWrapper) return <>{leafWrapper(block, content)}</>;
    if (block.widthPct === undefined) return content;
    return (
      <div
        className={styles.leafSizer}
        data-align={block.align ?? 'left'}
        style={{ '--rf-leaf-width': `${block.widthPct}%` } as CSSProperties}
      >
        {content}
      </div>
    );
  }

  const templateColumns = block.columns
    .map((column) => `${column.item.position.width}fr`)
    .join(' ');

  return (
    <div
      id={block.anchorId}
      className={classNames(
        styles.columns,
        block.columns.every((column) => column.kind !== 'text') &&
          styles.columnsTight
      )}
      style={{ '--rf-columns': templateColumns } as CSSProperties}
      data-testid="report-flow-columns-block"
    >
      {block.columns.map((column) =>
        columnWrapper ? (
          <Fragment key={column.id}>
            {columnWrapper(
              column,
              <LeafBlockView
                leaf={column}
                onChartClick={onChartClick}
                renderLeafContent={renderLeafContent}
              />
            )}
          </Fragment>
        ) : (
          <LeafBlockView
            key={column.id}
            leaf={column}
            onChartClick={onChartClick}
            renderLeafContent={renderLeafContent}
          />
        )
      )}
    </div>
  );
};
